import { css } from '@emotion/react';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useShallow } from 'zustand/shallow';
import SendButtonImg from '../../assets/chat_send.svg';
import { getChannelListener } from '../ChannelListener';
import { UIColorPalette } from '../ColorPalette';
import { User } from '../GraphQLHelpers';
import { ChatMessage, useChatStore } from '../stores/chatStore';
import { useSessionStore } from '../stores/sessionStore';
import { useUserStore } from '../stores/userStore';
import { MHoverButton } from './MButton';
import { MModal } from './MUIComponents';

function MChatButton(props: { room: string; title: string }) {
  const session = useSessionStore((state) => state.session);
  const messagesByRoom = useChatStore((state) => state.messagesByRoom);
  const { userByID, fetchUsers } = useUserStore(
    useShallow((state) => ({
      userByID: state.userByID,
      fetchUsers: state.fetchUsers,
    })),
  );

  if (!session) {
    return null;
  }

  useEffect(() => {
    const topic = `chat/${props.room}`;
    getChannelListener().subscribeToTopic(topic);
    return () => {
      getChannelListener().unsubscribeToTopic(topic);
    };
  }, [props.room]);

  const [expanded, setExpanded] = useState(false);

  const messages = messagesByRoom[props.room] || [];

  return (
    <>
      <MHoverButton
        onShortPress={() => setExpanded(true)}
        onLongPress={() => setExpanded(true)}
        onLongPressEnd={() => setExpanded(false)}
      >
        CHAT
      </MHoverButton>

      {expanded && (
        <MModal
          show={expanded}
          onClose={() => setExpanded(false)}
          title={props.title}
        >
          <MChatView
            room={props.room}
            title={props.title}
            messages={messages}
            userByID={userByID}
            fetchUsers={fetchUsers}
          />
        </MModal>
      )}
    </>
  );
}

export default MChatButton;

function MChatView(props: {
  room: string;
  title: string;
  messages: ChatMessage[];
  userByID: Record<string, User>;
  fetchUsers: (userIDs: string[]) => void;
}) {
  return (
    <div css={MChatStyles.container}>
      <MChatBody
        messages={props.messages}
        userByID={props.userByID}
        fetchUsers={props.fetchUsers}
      />
      <MChatForm room={props.room} />
    </div>
  );
}

const MChatForm = forwardRef(
  (
    props: {
      room: string;
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      },
    }));

    return (
      <form
        css={MChatStyles.form}
        onSubmit={(e) => {
          e.preventDefault();

          if (!inputRef.current) {
            return;
          }
          let message = inputRef.current.value;
          inputRef.current.value = '';

          message = message.trim();
          if (!message) {
            return;
          }

          getChannelListener().sendChatMessage(props.room, message);
        }}
      >
        <input
          css={MChatStyles.input}
          type="text"
          ref={inputRef}
          placeholder="Aa"
        />
        <button css={MChatStyles.sendButton} type="submit">
          <img css={MChatStyles.sendButtonImage} src={SendButtonImg} />
        </button>
      </form>
    );
  },
);

function MChatBody(props: {
  userByID: Record<string, User>;
  messages: ChatMessage[];
  fetchUsers: (userIDs: string[]) => void;
}) {
  const { userByID, messages, fetchUsers } = props;
  return (
    <div
      ref={(node) => {
        if (node) {
          node.scrollTop = node.scrollHeight;
        }
      }}
      css={MChatStyles.body}
    >
      {messages.map((message, i) => {
        let user = userByID[message.senderID];
        if (!user) {
          fetchUsers([message.senderID]);
        }
        return (
          <div key={i}>
            {user ? user.name : 'unknown'}: {message.message}
          </div>
        );
      })}
    </div>
  );
}

const MChatStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    backgroundColor: UIColorPalette.chatOverlayBG,
    flexGrow: 1,
    flexShrink: 1,
  }),
  header: css({}),
  body: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-end',

    flexGrow: 1,

    padding: 10,
  }),
  form: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'stretch',
    gap: 5,
    padding: 5,
  }),
  input: css({
    flexGrow: 1,
    height: 30,
  }),
  sendButton: css({
    flexGrow: 0,
  }),
  sendButtonImage: css({
    width: 30,
    margin: 5,
  }),
};
